/**
 * Typed bindings for /api/scraper + /api/scraped-users.
 *
 * Sprint 8: kicks off the harvest, lists users, lists job history.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { CHANNELS_KEY } from "./channels";

export type LastOnlineKind =
  | "online"
  | "offline"
  | "recently"
  | "last_week"
  | "last_month"
  | "long_ago"
  | "hidden";

export interface ScrapedUser {
  id: string;
  tg_user_id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  language_code: string | null;
  is_premium: boolean | null;
  is_bot: boolean;
  is_deleted: boolean;
  first_seen_channel_id: string | null;
  channel_ids: string[]; // ALL channels this user was scraped from
  last_seen_at: string | null;
  last_online_kind: LastOnlineKind | null;
  activity_score: number | null;
  scraped_at: string;
  scraper_account_id: string | null;
  /** Set when the person arrived through an import: where the list came from. */
  import_label: string | null;
  /** Key of the upload this person came from — same id the source list uses,
   * so the badge in the table can filter by it. */
  import_source: string | null;
  /** The "source" column of the uploaded file for THIS person, when the file
   * had one — finer-grained than the batch label. */
  import_row_source: string | null;
}

export interface ScrapedUsersPage {
  total: number;
  items: ScrapedUser[];
}

export interface ScrapeJob {
  id: string;
  channel_id: string;
  scraper_account_id: string | null;
  mode: "append" | "full_rescan";
  status: "queued" | "running" | "done" | "failed" | "cancelled";
  started_at: string | null;
  finished_at: string | null;
  users_seen: number;
  users_added: number;
  users_updated: number;
  users_skipped: number;
  error: string | null;
  created_at: string;
}

export interface UsersFilter {
  channel_id?: string;
  /** User appears in ANY of these channels (union). */
  in_any_channels?: string[];
  /** User appears in ALL of these channels (intersection). */
  in_all_channels?: string[];
  /** Uploaded lists the person came from — the operator's label, or the batch
   * id when they gave none. Unions with `in_any_channels`. */
  from_imports?: string[];
  min_activity?: number;
  has_username?: boolean;
  is_bot?: boolean;
  is_premium?: boolean;
  online_recently?: boolean;
  language?: string;
  search?: string;
  sort?: "activity" | "last_seen_desc" | "last_seen_asc";
  limit?: number;
  offset?: number;
}

const SCRAPED_USERS_KEY = "scraped_users";
const SCRAPE_JOBS_KEY = ["scrape_jobs"] as const;

function buildUsersQuery(filter: UsersFilter): string {
  const params = new URLSearchParams();
  if (filter.channel_id) params.set("channel_id", filter.channel_id);
  for (const id of filter.in_any_channels ?? []) params.append("in_any_channels", id);
  for (const id of filter.in_all_channels ?? []) params.append("in_all_channels", id);
  for (const key of filter.from_imports ?? []) params.append("from_imports", key);
  if (filter.min_activity != null)
    params.set("min_activity", String(filter.min_activity));
  if (filter.has_username != null)
    params.set("has_username", String(filter.has_username));
  if (filter.is_bot != null) params.set("is_bot", String(filter.is_bot));
  if (filter.is_premium != null)
    params.set("is_premium", String(filter.is_premium));
  if (filter.online_recently != null)
    params.set("online_recently", String(filter.online_recently));
  if (filter.language) params.set("language", filter.language);
  if (filter.search) params.set("search", filter.search);
  if (filter.sort) params.set("sort", filter.sort);
  if (filter.limit != null) params.set("limit", String(filter.limit));
  if (filter.offset != null) params.set("offset", String(filter.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function useScrapedUsers(filter: UsersFilter = {}) {
  return useQuery<ScrapedUsersPage>({
    queryKey: [SCRAPED_USERS_KEY, filter],
    queryFn: () =>
      api.get<ScrapedUsersPage>(`/api/scraped-users${buildUsersQuery(filter)}`),
  });
}

export function useDeleteScrapedUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/scraped-users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === SCRAPED_USERS_KEY });
      qc.invalidateQueries({ queryKey: ["audience_sources"] });
    },
  });
}

/** A run is executed by the worker; the request only dispatches it. */
export interface RunQueued {
  queued: boolean;
  /** True when a run was already in flight — nothing new was started. */
  already_running: boolean;
  channels: number;
}

export interface RunStatus {
  running: boolean;
  queued_channels: number;
  parsing_channels: number;
}

const RUN_STATUS_KEY = ["scraper_run_status"] as const;

export function useRunScraper() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      mode?: "append" | "full_rescan";
      max_channels?: number;
      max_users_per_channel?: number;
      use_live_telethon?: boolean;
      include_reaction_authors?: boolean;
      include_mentions?: boolean;
      since_days?: number | null;
      skip_bots?: boolean;
      auto_join?: boolean;
      /** How many accounts split the queue (1-3). */
      accounts?: number;
    }) => api.post<RunQueued>("/api/scraper/run", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHANNELS_KEY });
      qc.invalidateQueries({ queryKey: SCRAPE_JOBS_KEY });
      qc.invalidateQueries({ queryKey: RUN_STATUS_KEY });
      qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === SCRAPED_USERS_KEY,
      });
      qc.invalidateQueries({ queryKey: ["audience_sources"] });
    },
  });
}

/**
 * Poll while a run is in flight. Runs are paced (minutes between channels), so
 * the operator needs a live signal that work is still happening — otherwise a
 * dispatched run looks identical to nothing happening at all.
 */
export function useRunStatus(pollMs: number | false = false) {
  return useQuery<RunStatus>({
    queryKey: RUN_STATUS_KEY,
    queryFn: () => api.get<RunStatus>("/api/scraper/run/status"),
    refetchInterval: pollMs,
  });
}

export function useRunScraperOne() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      ...payload
    }: {
      channelId: string;
      mode?: "append" | "full_rescan";
      max_users_per_channel?: number;
      use_live_telethon?: boolean;
      include_reaction_authors?: boolean;
      since_days?: number | null;
      skip_bots?: boolean;
      auto_join?: boolean;
    }) =>
      api.post<ScrapeJob>(
        `/api/scraper/channels/${channelId}/run`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHANNELS_KEY });
      qc.invalidateQueries({ queryKey: SCRAPE_JOBS_KEY });
      qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === SCRAPED_USERS_KEY,
      });
      qc.invalidateQueries({ queryKey: ["audience_sources"] });
    },
  });
}

export function useScrapeJobs(channelId?: string, pollMs: number | false = false) {
  return useQuery<ScrapeJob[]>({
    queryKey: channelId ? ["scrape_jobs", channelId] : SCRAPE_JOBS_KEY,
    queryFn: () => {
      const qs = channelId ? `?channel_id=${channelId}` : "";
      return api.get<ScrapeJob[]>(`/api/scraper/jobs${qs}`);
    },
    refetchInterval: pollMs,
  });
}
